import type React from 'react'
import { useCallback, useEffect } from 'react'
import { toast } from 'sonner'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { anchorsToSvgPath } from '@/lib/elements/pen-path-utils'
import { deepClonePoints } from '../helpers/anchor-points'
import { insertNodeAtSegment } from '../helpers/insert-node-at-segment'
import { type RemoveAnchorResult, removeAnchor } from '../helpers/remove-anchor'
import { resolveContinueEnd } from '../helpers/subpath-ranges'
import type { NodeEditOverlayProps } from '../types/node-edit-overlay-props'
import type { NodeEditPropRefs } from './use-node-edit-prop-refs'
import type { NodePathState } from './use-node-path-state'
import type { PenEditHover } from './use-pen-edit-hover'

type PathCallbacks = Pick<
  NodeEditOverlayProps,
  'onPathChange' | 'onPathCommit' | 'onContinuePath' | 'onNodeAdded'
>

interface UsePenEditModeOptions extends PathCallbacks {
  isPenEditMode: boolean
  shape: ShapeData
  geometryInteractionDisabled: boolean
  path: NodePathState
  propRefs: NodeEditPropRefs
  hover: PenEditHover
}

/**
 * Pen-edit sub-mode clicks: continue an open path from an endpoint, remove an anchor, or insert a
 * node on a segment. Handled by a window-level capture listener (see below) and, as a fallback,
 * by the capture rect's own `onPointerDown` (`handlePenEditClick`).
 */
export function usePenEditMode({
  isPenEditMode,
  shape,
  geometryInteractionDisabled,
  path,
  propRefs,
  hover,
  onPathChange,
  onPathCommit,
  onContinuePath,
  onNodeAdded,
}: UsePenEditModeOptions) {
  const { pointsRef, isClosedRef, subpathsRef, forceUpdate } = path
  const { penHoverRef, setPenHover, computePenHoverRef } = hover
  const { onPenHoverChangeRef, geometryInteractionDisabledRef } = propRefs

  /** Applies a successful anchor removal and reports the new path as a change, then a commit.
   *  The reporters resolve their callback (and shape id) at call time. */
  function applyRemoval(
    removal: Extract<RemoveAnchorResult, { kind: 'removed' }>,
    reportChange: (pathD: string) => void,
    reportCommit: (pathD: string) => void
  ) {
    pointsRef.current = removal.points
    subpathsRef.current = removal.subpaths
    isClosedRef.current = removal.isClosed
    // Clear stale hover so render doesn't access an out-of-bounds index
    penHoverRef.current = null
    setPenHover(null)
    forceUpdate((n) => n + 1)
    reportChange(anchorsToSvgPath(removal.points, isClosedRef.current, removal.subpaths))
    reportCommit(anchorsToSvgPath(removal.points, isClosedRef.current, removal.subpaths))
  }

  /** Applies a node insertion and reports the new path as a change, then a commit, then a
   *  node-added notification. */
  function applyInsertion(
    insertion: ReturnType<typeof insertNodeAtSegment>,
    reportChange: (pathD: string) => void,
    reportCommit: (pathD: string) => void,
    reportNodeAdded: () => void
  ) {
    pointsRef.current = insertion.points
    subpathsRef.current = insertion.subpaths
    forceUpdate((n) => n + 1)
    reportChange(anchorsToSvgPath(insertion.points, isClosedRef.current, insertion.subpaths))
    reportCommit(anchorsToSvgPath(insertion.points, isClosedRef.current, insertion.subpaths))
    reportNodeAdded()
  }

  // Window-level event listeners for pen-edit mode.
  // The SVG element's hit-test boundary in the HTML event model is its CSS layout box
  // (bleedWidth × bleedHeight). Even with overflow:visible, pointer events from the
  // pasteboard (outside that box) are never routed to the SVG or any of its children.
  // Window-level capture fires for every pointer event regardless of cursor position,
  // mirroring the approach in pen-tool-overlay/hooks/use-pen-pointer-listeners.ts.
  useEffect(() => {
    if (!isPenEditMode) return

    const reportChange = (pathD: string) =>
      propRefs.onPathChangeRef.current(propRefs.shapeIdRef.current, pathD)
    const reportCommit = (pathD: string) =>
      propRefs.onPathCommitRef.current(propRefs.shapeIdRef.current, pathD)

    const onMove = (e: PointerEvent) => {
      const next = computePenHoverRef.current(e.clientX, e.clientY)

      penHoverRef.current = next
      setPenHover(next)
      onPenHoverChangeRef.current?.(next ? next.kind : null)
    }

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      if (geometryInteractionDisabledRef.current) return

      // Recompute hover at the exact click position. penHoverRef may have been nulled by
      // handlePenEditLeave if the cursor crossed the bleed boundary just before the click
      // (e.g. moving from the spread to a pasteboard node and clicking without an intervening
      // pointermove). Reading a fresh hit avoids silently swallowing the click.
      const target = computePenHoverRef.current(e.clientX, e.clientY)

      if (!target) return

      // Update ref so subsequent logic (and the <rect> fallback) sees the same target.
      penHoverRef.current = target

      // There is a hit target — consume the event so it doesn't reach shapes below.
      e.preventDefault()
      e.stopPropagation()

      const pts = pointsRef.current
      const subpaths = subpathsRef.current

      if (target.kind === 'endpoint') {
        propRefs.onContinuePathRef.current?.(
          resolveContinueEnd(subpaths, target.index),
          deepClonePoints(pts)
        )
        return
      }

      if (target.kind === 'anchor') {
        const removal = removeAnchor(pts, subpaths, target.index, {
          isImageContainer:
            propRefs.shapeKindIdRef.current === 4 || propRefs.hasImageInsideRef.current,
          isTextContainer: propRefs.hasTextInsideRef.current,
        })

        if (removal.kind === 'blocked') {
          toast.warning(removal.message)
          return
        }

        if (removal.kind === 'unchanged') return

        applyRemoval(removal, reportChange, reportCommit)
        return
      }

      if (target.kind === 'segment') {
        applyInsertion(
          insertNodeAtSegment(pts, subpaths, target.segmentIndex, target.t),
          reportChange,
          reportCommit,
          () => propRefs.onNodeAddedRef.current?.()
        )
      }
    }

    window.addEventListener('pointermove', onMove, { capture: true, passive: true })
    window.addEventListener('pointerdown', onDown, { capture: true })

    return () => {
      window.removeEventListener('pointermove', onMove, { capture: true })
      window.removeEventListener('pointerdown', onDown, { capture: true })
      // Reset hover so stale state doesn't linger after exiting pen-edit mode.
      penHoverRef.current = null
      setPenHover(null)
      propRefs.onPenHoverChangeRef.current?.(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPenEditMode])

  const handlePenEditClick = useCallback(
    (e: React.PointerEvent) => {
      if (geometryInteractionDisabled) return
      if (e.button !== 0) return
      e.preventDefault()
      e.stopPropagation()

      const target = penHoverRef.current
      const pts = pointsRef.current
      const subpaths = subpathsRef.current

      if (!target) return

      if (target.kind === 'endpoint') {
        // Continue drawing from this endpoint
        onContinuePath?.(resolveContinueEnd(subpaths, target.index), deepClonePoints(pts))
        return
      }

      if (target.kind === 'anchor') {
        // Remove the anchor (see `removeAnchor`). Unlike the window listener above, this path
        // treats only a photo fill as an image container (no legacy kindId-4 check).
        const removal = removeAnchor(pts, subpaths, target.index, {
          isImageContainer: Boolean(shape.photoFill),
          // Presence, not content (WMPCM-8969): an emptied text frame is still a text frame, and
          // letting it drop to an open 2-node line makes `isShapeTextSupported` false — silently
          // stripping the frame identity. Deliberately not `isShapeTextFrame`, which also tests
          // `isShapeTextSupported` against `shape.path`; that can be transient mid-node-edit here
          // (closedness is tracked separately via `subpathsRef`).
          isTextContainer: shape.paragraphs != null,
        })

        if (removal.kind === 'blocked') {
          toast.warning(removal.message)
          return
        }

        if (removal.kind === 'unchanged') return

        applyRemoval(
          removal,
          (pathD) => onPathChange(shape.id, pathD),
          (pathD) => onPathCommit(shape.id, pathD)
        )
        return
      }

      if (target.kind === 'segment') {
        // Add a node by splitting the segment at t
        applyInsertion(
          insertNodeAtSegment(pts, subpaths, target.segmentIndex, target.t),
          (pathD) => onPathChange(shape.id, pathD),
          (pathD) => onPathCommit(shape.id, pathD),
          () => onNodeAdded?.()
        )
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shape.id, onPathChange, onPathCommit, onContinuePath, onNodeAdded]
  )

  return { handlePenEditClick }
}
